import type {
  CompanyNoteDto,
  CreateCompanyNoteDto,
  UpdateCompanyNoteDto,
} from '@schwrz/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '../shared/api/client';
import { companyNoteDetailsParam } from '../shared/lib/note-route';

export const useCompanyNotesStore = defineStore('companyNotes', () => {
  /** Cache keyed by clientId. */
  const byClient = ref(new Map<string, CompanyNoteDto[]>());
  /** Single-note cache by id (for the details page). */
  const byId = ref(new Map<string, CompanyNoteDto>());
  const loadingClients = ref(new Set<string>());

  function listOf(clientId: string): CompanyNoteDto[] {
    return byClient.value.get(clientId) ?? [];
  }

  function setListFor(clientId: string, notes: CompanyNoteDto[]): void {
    const next = new Map(byClient.value);
    next.set(clientId, notes);
    byClient.value = next;
  }

  function indexById(n: CompanyNoteDto): void {
    const next = new Map(byId.value);
    next.set(n.id, n);
    next.set(String(n.number), n);
    byId.value = next;
  }

  async function loadForClient(clientId: string, force = false): Promise<void> {
    if (loadingClients.value.has(clientId)) return;
    if (byClient.value.has(clientId) && !force) return;
    loadingClients.value.add(clientId);
    try {
      const res = await api<{ notes: CompanyNoteDto[] }>(
        `/api/clients/${encodeURIComponent(clientId)}/notes`,
      );
      setListFor(clientId, res.notes);
      for (const n of res.notes) indexById(n);
    } finally {
      loadingClients.value.delete(clientId);
    }
  }

  async function fetchOne(id: string): Promise<CompanyNoteDto> {
    const note = await api<CompanyNoteDto>(`/api/company-notes/${encodeURIComponent(id)}`);
    indexById(note);
    const bucket = listOf(note.clientId);
    if (bucket.length > 0) {
      setListFor(
        note.clientId,
        bucket.some((n) => n.id === note.id)
          ? bucket.map((n) => (n.id === note.id ? note : n))
          : [note, ...bucket],
      );
    }
    return note;
  }

  async function create(clientId: string, payload: CreateCompanyNoteDto): Promise<CompanyNoteDto> {
    const created = await api<CompanyNoteDto>(
      `/api/clients/${encodeURIComponent(clientId)}/notes`,
      { method: 'POST', body: payload },
    );
    indexById(created);
    const bucket = listOf(clientId);
    setListFor(clientId, [created, ...bucket]);
    return created;
  }

  async function update(id: string, payload: UpdateCompanyNoteDto): Promise<CompanyNoteDto> {
    const updated = await api<CompanyNoteDto>(`/api/company-notes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload,
    });
    indexById(updated);
    const bucket = listOf(updated.clientId);
    if (bucket.length > 0) {
      setListFor(
        updated.clientId,
        bucket.map((n) => (n.id === updated.id ? updated : n)),
      );
    }
    return updated;
  }

  async function remove(note: CompanyNoteDto): Promise<void> {
    await api<void>(`/api/company-notes/${encodeURIComponent(companyNoteDetailsParam(note))}`, {
      method: 'DELETE',
    });
    const map = new Map(byId.value);
    map.delete(note.id);
    map.delete(String(note.number));
    byId.value = map;
    const bucket = listOf(note.clientId);
    setListFor(
      note.clientId,
      bucket.filter((n) => n.id !== note.id),
    );
  }

  return {
    byClient,
    byId,
    loadingClients,
    listOf,
    loadForClient,
    fetchOne,
    create,
    update,
    remove,
  };
});
